import React, { ErrorInfo } from 'react'
import {
  ErrorBoundary as ReactErrorBoundary,
  FallbackProps,
} from 'react-error-boundary'
import { Box, Button, Typography } from '@mui/material'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { RADIUS } from '@/shared/styles/radii'
import { NOTION_BUTTON_PRIMARY_SX } from '@/shared/styles/buttonStyles'
import { UI_FONT_SIZE, typeScale } from '@/shared/styles/typography'

const STEPS = [
  {
    title: 'スーパーリロードを試す',
    body: (
      <>
        Windows: <code>Ctrl + Shift + R</code> または <code>Ctrl + F5</code>
        <br />
        Mac: <code>Cmd + Shift + R</code>
      </>
    ),
  },
  {
    title: 'ブラウザのキャッシュを削除する',
    body: <>ブラウザの設定からキャッシュと Cookie を削除してください。</>,
  },
] as const

const StepItem = ({
  index,
  title,
  body,
}: {
  index: number
  title: string
  body: React.ReactNode
}) => (
  <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
    <Typography
      sx={{
        flexShrink: 0,
        fontSize: typeScale.body.fontSize,
        color: colorTokens.fg.muted,
        minWidth: 16,
      }}
    >
      {index}.
    </Typography>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: typeScale.body.fontSize,
          fontWeight: typeScale.body.fontWeight,
          lineHeight: typeScale.body.lineHeight,
          color: colorTokens.fg.default,
        }}
      >
        {title}
      </Typography>
      <Typography
        component="div"
        sx={{
          fontSize: typeScale.bodySm.fontSize,
          lineHeight: typeScale.bodySm.lineHeight,
          color: colorTokens.fg.muted,
          mt: 0.25,
          '& code': {
            fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
            fontSize: UI_FONT_SIZE.tag,
            px: 0.75,
            py: 0.25,
            borderRadius: `${RADIUS.sm}px`,
            backgroundColor: colorTokens.bg.subtle,
            color: colorTokens.fg.default,
          },
        }}
      >
        {body}
      </Typography>
    </Box>
  </Box>
)

export const ErrorFallback = ({ error: _error }: FallbackProps) => (
  <Box
    sx={{
      minHeight: '100vh',
      backgroundColor: colorTokens.bg.default,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      px: { xs: 3, sm: 4 },
      py: { xs: 6, sm: 10 },
    }}
  >
    <Box sx={{ width: '100%', maxWidth: 520 }}>
      <Typography
        component="h1"
        sx={{
          fontSize: typeScale.h1.fontSize,
          fontWeight: typeScale.h1.fontWeight,
          lineHeight: typeScale.h1.lineHeight,
          letterSpacing: typeScale.h1.letterSpacing,
          color: colorTokens.fg.default,
          mb: 1.5,
        }}
      >
        接続に問題が発生しました
      </Typography>
      <Typography
        sx={{
          fontSize: typeScale.body.fontSize,
          lineHeight: typeScale.body.lineHeight,
          color: colorTokens.fg.muted,
          mb: 4,
        }}
      >
        一時的な問題が発生している可能性があります。以下の手順をお試しください。
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
        {STEPS.map((step, i) => (
          <StepItem
            key={step.title}
            index={i + 1}
            title={step.title}
            body={step.body}
          />
        ))}
      </Box>

      <Typography
        sx={{
          fontSize: typeScale.bodySm.fontSize,
          lineHeight: typeScale.bodySm.lineHeight,
          color: colorTokens.fg.muted,
          mb: 3,
        }}
      >
        解決しない場合はサポート窓口にお問い合わせください。
      </Typography>

      <Button
        variant="contained"
        color="primary"
        disableElevation
        onClick={() => {
          localStorage.clear()
          sessionStorage.clear()
          window.location.href = '/'
        }}
        sx={NOTION_BUTTON_PRIMARY_SX}
      >
        メイン画面に戻る
      </Button>
    </Box>
  </Box>
)

export const ErrorBoundary = ({ children }: { children: React.ReactNode }) => (
  <ReactErrorBoundary
    FallbackComponent={ErrorFallback}
    onError={(_error: Error, _info: ErrorInfo) => {
      // Error logging handled elsewhere
    }}
  >
    {children}
  </ReactErrorBoundary>
)
